import { Button } from '@/components/ui/button';
import { SuggestionItem } from '@/features/search-bar/components/suggestion-item';
import type { useSearchHistory } from '@/features/search-bar/hooks/use-search-history';
import type { HistoryItem, Suggestion } from '@/features/search-bar/types';
import { formatHistoryDate } from '@/features/search-bar/utils';
import Icon from '@/icons/icon';

interface Props {
    inputRef: React.RefObject<HTMLInputElement | null>;
    query: string;
    suggestions: Suggestion[];
    loadingSuggestions: boolean;
    history: ReturnType<typeof useSearchHistory>;
    onQueryChange: (value: string) => void;
    onSubmit: () => void;
    onClear: () => void;
    onSelectSuggestion: (suggestion: Suggestion) => void;
    onSelectHistory: (item: HistoryItem) => void;
}

export function MobileSearchOverlay({
    inputRef: mobileInputRef,
    query,
    suggestions,
    loadingSuggestions,
    history,
    onQueryChange,
    onSubmit,
    onClear,
    onSelectSuggestion,
    onSelectHistory,
}: Props) {
    const {
        searchHistory,
        showAllHistory,
        setShowAllHistory,
        removeFromHistory,
    } = history;

    return (
        <div className="fixed inset-x-0 top-16 bottom-0 z-10000 flex flex-col bg-card sm:hidden">
            {/* Header */}
            <div className="flex shrink-0 items-center border-b border-gray-200 px-4 py-3">
                <div className="relative w-full">
                    <Icon
                        iconName="Location"
                        className="absolute top-1/2 left-2 h-4 w-4 shrink-0 -translate-y-1/2 fill-gray-400"
                    />
                    <input
                        ref={mobileInputRef}
                        autoFocus
                        className="w-full rounded-lg bg-gray-100 py-2 pr-8 pl-8 text-base font-semibold text-black outline-none dark:text-foreground"
                        placeholder="Where are you going?"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                onSubmit();
                            }
                        }}
                    />
                    <Button
                        variant="bare"
                        type="button"
                        onClick={onClear}
                        className="absolute top-1/2 right-2 -translate-y-1/2"
                    >
                        <Icon
                            iconName="X"
                            className="h-6 w-6 stroke-gray-500 hover:stroke-gray-600"
                        />
                    </Button>
                </div>
            </div>

            {/* Suggestions list — normal flow, always visible above keyboard */}
            <div className="flex-1 overflow-y-auto">
                {loadingSuggestions ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                        Loading suggestions...
                    </div>
                ) : !query.trim() && searchHistory.length > 0 ? (
                    <div>
                        <div className="px-4 pt-4 pb-2 text-sm font-bold tracking-wide text-gray-900">
                            Your recent searches
                        </div>
                        <ul>
                            {(showAllHistory
                                ? searchHistory
                                : searchHistory.slice(0, 3)
                            ).map((item) => (
                                <li
                                    key={item.id}
                                    onClick={() => onSelectHistory(item)}
                                    className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-4 py-3 transition hover:bg-gray-50"
                                >
                                    <Icon
                                        iconName="HistoryClock"
                                        className="h-6 w-6 shrink-0 stroke-gray-500"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium text-gray-900">
                                            {item.query}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {[
                                                item.startDate && item.endDate
                                                    ? `${formatHistoryDate(item.startDate)} – ${formatHistoryDate(item.endDate)}`
                                                    : null,
                                                item.adults > 0
                                                    ? `${item.adults} adult${item.adults > 1 ? 's' : ''}`
                                                    : null,
                                            ]
                                                .filter(Boolean)
                                                .join(' , ')}
                                        </div>
                                    </div>
                                    <Button
                                        variant="bare"
                                        type="button"
                                        onClick={(e) =>
                                            removeFromHistory(item.id, e)
                                        }
                                        className="shrink-0 cursor-pointer rounded p-1 hover:bg-gray-200"
                                    >
                                        <Icon
                                            iconName="X"
                                            className="h-6 w-6 stroke-gray-500"
                                        />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                        {searchHistory.length > 3 && (
                            <Button
                                variant="bare"
                                type="button"
                                onClick={() => setShowAllHistory((v) => !v)}
                                className="w-full px-4 py-3 text-left text-sm font-medium text-blue-600 hover:bg-gray-50"
                            >
                                {showAllHistory ? 'Show less' : 'Show more'}
                            </Button>
                        )}
                    </div>
                ) : suggestions.length > 0 ? (
                    <ul>
                        {suggestions.map((suggestion) => (
                            <SuggestionItem
                                key={`${suggestion.type}-${suggestion.id}`}
                                s={suggestion}
                                onClick={() => onSelectSuggestion(suggestion)}
                            />
                        ))}
                    </ul>
                ) : query.trim() ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                        No suggestions found
                    </div>
                ) : null}
            </div>
        </div>
    );
}
